import { useCallback, useRef, useState } from "react";
import {
  PropertyData,
  capturePropertyElement,
  downloadPropertyCapture,
  PropertyCaptureResult,
  ImageFormat,
} from "../utils/propertyCapture";
import { sharePropertyDataToWhatsApp } from "../utils/shareViaWhatsApp";
import { shareVia } from "../utils/shareVia";
import {
  createFile,
  getFileByOwnerIdentifier,
  sharePropertyBrochure,
} from "services";
import { useSelector } from "react-redux";
import { useGetProfileDetails } from "../../../../../hoc";

export type PropertyShareFormat = ImageFormat | "pdf";
export type PropertyShareType =
  | "share-via-whatsapp"
  | "share-via"
  | "download-brochure";

export interface PropertyShareState {
  isCapturing: boolean;
  isSharing: boolean;
  captureResult: PropertyCaptureResult | null;
  error: string | null;
  statusMessage: string | null;
}

export interface PropertyShareOptions {
  format?: PropertyShareFormat;
  propertyUrl?: string;
  brochureImageUrl?: string;
  file?: File;
  uploadToS3?: boolean;
}

export interface UsePropertyShareReturn {
  cardRef: React.RefObject<HTMLDivElement>;
  state: PropertyShareState;
  share: (
    type: PropertyShareType,
    options?: PropertyShareOptions,
  ) => Promise<void>;
  captureCard: (
    format?: PropertyShareFormat,
  ) => Promise<PropertyCaptureResult | null>;
  downloadCard: () => void;
  reset: () => void;
}

const initialState: PropertyShareState = {
  isCapturing: false,
  isSharing: false,
  captureResult: null,
  error: null,
  statusMessage: null,
};

export const usePropertyShare = (
  property: PropertyData,
): UsePropertyShareReturn => {
  const cardRef = useRef<HTMLDivElement>(null);
  const [state, setState] = useState(initialState);
  const isLoggedIn = useSelector((state: any) => state.auth.user);
  const { mobile } = useGetProfileDetails();

  const showStatusMessage = useCallback((message: string, duration = 3000) => {
    setState((prev) => ({ ...prev, statusMessage: message }));
    setTimeout(() => {
      setState((prev) => ({ ...prev, statusMessage: null }));
    }, duration);
  }, []);

  const captureCard = useCallback(
    async (
      format: PropertyShareFormat = "png",
    ): Promise<PropertyCaptureResult | null> => {
      const element =
        cardRef.current ||
        (document.getElementById(
          "property-card-for-capture",
        ) as HTMLDivElement | null);

      if (!element) {
        setState((prev) => ({
          ...prev,
          error: "Card element not found",
        }));
        return null;
      }

      setState((prev) => ({
        ...prev,
        isCapturing: true,
        error: null,
        statusMessage: "Creating property card...",
      }));

      try {
        const result = await capturePropertyElement(element, {
          format,
          fileName: `${property?.auctionId
            .replace(/[^a-zA-Z0-9]/g, "-")
            .toLowerCase()}-brochure-${property?.uuid}`,
          quality: 0.92,
          scale: 2,
        });

        setState((prev) => ({
          ...prev,
          isCapturing: false,
          captureResult: result,
          statusMessage: null,
        }));

        return result;
      } catch (error: any) {
        setState((prev) => ({
          ...prev,
          isCapturing: false,
          error: error?.message || "Capture failed",
          statusMessage: null,
        }));

        return null;
      }
    },
    [property?.auctionId, property?.uuid],
  );

  const downloadCard = useCallback(() => {
    if (!state.captureResult) return;

    downloadPropertyCapture(state.captureResult);
    showStatusMessage("Property card downloaded");
  }, [state.captureResult, showStatusMessage]);

  const uploadBrochureToS3 = useCallback(
    async (
      file: File,
      propertyUuid: string,
    ): Promise<{
      propertyUuid: string;
      propertyBrochureImageUrl: string;
      propertyBrochureFileId: string;
    } | null> => {
      let brochureFileResponse = {
        propertyUuid: propertyUuid,
        propertyBrochureImageUrl: "",
        propertyBrochureFileId: "",
      };
      const ownerIdentifier = `${property?.propertyType}-brochure-file-${propertyUuid}`;
      const existingPropertyBrochureFileResponse: any =
        await getFileByOwnerIdentifier(ownerIdentifier);

      if (existingPropertyBrochureFileResponse?.uuid) {
        return {
          ...brochureFileResponse,
          propertyBrochureImageUrl: `${process.env.NEXT_PUBLIC_IMAGIX_URL}/${existingPropertyBrochureFileResponse?.uuid}?fm=png`,
          propertyBrochureFileId: existingPropertyBrochureFileResponse?.uuid,
        };
      } else {
        try {
          const uploadResponse: any = await createFile(file, {
            owner_identifier: ownerIdentifier,
            owner_service: "property-brochure",
          });

          return {
            ...brochureFileResponse,
            propertyBrochureImageUrl: `${process.env.NEXT_PUBLIC_IMAGIX_URL}/${uploadResponse?.uuid}?fm=png`,
            propertyBrochureFileId: uploadResponse?.uuid,
          };
        } catch (error) {
          console.error("Failed to upload brochure to S3:", error);
          return {
            ...brochureFileResponse,
            propertyBrochureImageUrl: "",
            propertyBrochureFileId: "",
          };
        }
      }
    },
    [property?.propertyType],
  );

  const handleShareViaWhatsAppForLoggedIn = useCallback(
    async ({ propertyBrochureFileId }: { propertyBrochureFileId: string }) => {
      try {
        const payload = {
          mobile: mobile,
          property_uuid: property?.uuid,
          property_document_uuid: propertyBrochureFileId,
        };

        await sharePropertyBrochure(payload);

        setState((prev) => ({
          ...prev,
          statusMessage: "Property Brochure shared successfully.",
        }));
      } catch (error) {
        console.error("🚀 ~ usePropertyShare ~ error:", error);

        setState((prev) => ({
          ...prev,
          statusMessage: "Failed to share Property Brochure. Please try again",
        }));
      }
    },
    [mobile, property?.uuid],
  );

  const share = useCallback(
    async (
      type: PropertyShareType,
      options: PropertyShareOptions = {},
    ): Promise<void> => {
      const { format = "png", propertyUrl, uploadToS3 = false } = options;

      setState((prev) => ({
        ...prev,
        isSharing: true,
        error: null,
        statusMessage: "Preparing share...",
      }));

      try {
        // Always capture fresh for sharing to ensure we have the latest data
        const captureResult = await captureCard(format);

        if (!captureResult) {
          setState((prev) => ({
            ...prev,
            isSharing: false,
            error: "Failed to capture property card",
            statusMessage: "Failed to create brochure. Please try again.",
          }));
          return;
        }

        let brochureImageUrl: string = "";
        let _propertyBrochureFileId: string = "";
        // Only upload to S3 if explicitly enabled (for getting a shareable URL)
        if (
          uploadToS3 &&
          (type === "share-via-whatsapp" || type === "share-via")
        ) {
          setState((prev) => ({
            ...prev,
            statusMessage: "Processing property brochure...",
          }));

          const data = await uploadBrochureToS3(
            captureResult.file,
            property?.uuid || "unknown",
          );

          brochureImageUrl = data?.propertyBrochureImageUrl as string;
          _propertyBrochureFileId = data?.propertyBrochureFileId as string;
        }

        switch (type) {
          case "share-via-whatsapp":
            if (isLoggedIn) {
              handleShareViaWhatsAppForLoggedIn({
                propertyBrochureFileId: _propertyBrochureFileId,
              });
            } else {
              sharePropertyDataToWhatsApp({
                property,
                propertyUrl,
                brochureImageUrl: brochureImageUrl || undefined,
              });
              showStatusMessage("WhatsApp opened");
            }
            break;

          case "share-via": {
            // Share with file attachment - the shareVia function handles mobile/desktop differences
            const shareResult = await shareVia({
              property,
              propertyUrl,
              file: captureResult.file,
              brochureImageUrl: brochureImageUrl || undefined,
            });

            if (!shareResult.success && brochureImageUrl) {
              // If file sharing failed and we have a URL, try with just the URL
              await shareVia({
                property,
                propertyUrl: brochureImageUrl,
              });
            }

            showStatusMessage("Share dialog opened");
            break;
          }

          case "download-brochure":
            downloadPropertyCapture(captureResult);
            showStatusMessage("Brochure downloaded");
            break;
        }

        setState((prev) => ({
          ...prev,
          isSharing: false,
          captureResult,
          statusMessage: null,
        }));
      } catch (error: any) {
        setState((prev) => ({
          ...prev,
          isSharing: false,
          error: error?.message || "Share failed",
          statusMessage: null,
        }));

        showStatusMessage("Share failed. Please try again.");
      }
    },
    [
      captureCard,
      handleShareViaWhatsAppForLoggedIn,
      isLoggedIn,
      property,
      showStatusMessage,
      uploadBrochureToS3,
    ],
  );

  const reset = useCallback(() => {
    setState(initialState);
  }, []);

  return {
    cardRef,
    state,
    share,
    captureCard,
    downloadCard,
    reset,
  };
};
